import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Card,
  CardContent,
  CardActions,
  Chip
} from "@mui/material";
import {
  Gavel,
  Handshake,
  InsertDriveFile,
  VideoCall,
  Chat,
  CheckCircle,
  Warning,
  FileDownload,
  UploadFile,
  Timeline,
  Shield,
  ArrowForward,
  Scale
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CaseApi from "Service/Case.api";
import OdrApi from "Service/Odr.api";
import AdminApi from "Service/Admin.api";
import { UserInitialStateType } from "DataTypes/Redux";
import AgoraCall from "Components/Conference/AgoraCall";
import { PremiumTimelineTracker } from "./PremiumTimelineTracker";

const STATUS_STEPS = [
  { label: "Initiated", description: "Legal notice served, 10-day response window started" },
  { label: "Respondent Consent", description: "Respondent reviews notice and consents to ODR" },
  { label: "Neutral Assignment", description: "Neutral panel expert / Mediator assigned" },
  { label: "Dialogue & Caucus", description: "Active online sessions, caucusing and evidence upload" },
  { label: "Finalization", description: "Settlement draft prepared, eSigned, and closed" }
];

const OdrWorkspace: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const numericCaseId = parseInt(caseId || "0");

  const { myInfo }: UserInitialStateType = useSelector((state: any) => state?.user);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [caseDetails, setCaseDetails] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [activeCall, setActiveCall] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [neutralAssignment, setNeutralAssignment] = useState<any>(null);
  const [disclosureText, setDisclosureText] = useState("");
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [selectedNeutralId, setSelectedNeutralId] = useState<number | string>("");

  useEffect(() => {
    if (numericCaseId > 0) {
      loadData();
    } else {
      setError("Invalid Case ID provided.");
      setLoading(false);
    }
  }, [numericCaseId]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      // 1. Fetch main case details
      const caseRes = await CaseApi.fetchCaseById({ caseId: numericCaseId });
      if (caseRes?.success && caseRes?.data) {
        setCaseDetails(caseRes.data);
      } else {
        setError("Could not retrieve case details. Please ensure you are authorized.");
      }

      // 2. Fetch ODR case documents
      const docsRes = await OdrApi.getOdrDocuments(numericCaseId);
      if (docsRes?.success && Array.isArray(docsRes.data)) {
        setDocuments(docsRes.data);
      } else if (Array.isArray(docsRes)) {
        setDocuments(docsRes);
      }

      // 3. Fetch scheduled video sessions
      const sessionsRes = await OdrApi.getOdrSessions(numericCaseId);
      if (sessionsRes?.success && Array.isArray(sessionsRes.data)) {
        setSessions(sessionsRes.data);
      } else if (Array.isArray(sessionsRes)) {
        setSessions(sessionsRes);
      }

      // 4. Fetch proposed neutral details
      try {
        const neutralRes = await OdrApi.getOdrNeutral(numericCaseId);
        let neutralData = null;
        if (neutralRes && neutralRes.id) {
          neutralData = neutralRes;
        } else if (neutralRes?.success && neutralRes.data) {
          neutralData = neutralRes.data;
        }
        setNeutralAssignment(neutralData);
      } catch (neutralErr) {
        console.log("No proposed neutral or failed to load neutral assignment details", neutralErr);
        setNeutralAssignment(null);
      }

      // 5. Fetch active professionals if user is Admin
      const isAdminUser = ["ADMIN", "SUPERADMIN"].includes(myInfo?.userType?.toUpperCase() || "");
      if (isAdminUser) {
        try {
          const profRes = await AdminApi.GetAllActiveProfessionalUser();
          if (profRes && profRes.data) {
            setProfessionals(profRes.data);
          } else if (Array.isArray(profRes)) {
            setProfessionals(profRes);
          }
        } catch (profErr) {
          console.error("Failed to load active professionals", profErr);
        }
      }
    } catch (err: any) {
      setError(err?.message || "An unexpected error occurred loading dispute room data.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async (filePath: string, fileName: string) => {
    if (!filePath) return;
    
    let absoluteUrl = filePath;
    if (!filePath.startsWith("http")) {
      const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
      absoluteUrl = `${baseUrl.replace(/\/$/, "")}/${filePath.replace(/^\//, "")}`;
    }

    try {
      const response = await fetch(absoluteUrl, {
        mode: "cors",
        cache: "no-cache",
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName || "Document");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.warn("Direct download failed, opening in new tab", err);
      window.open(absoluteUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleDownloadNotice = async () => {
    try {
      const res = await OdrApi.downloadNoticePdf(numericCaseId);
      if (res && res.data) {
        const blob = res.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `ODR_Notice_Brief_${numericCaseId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Notice PDF download failed", err);
    }
  };

  const handleRespondentConsent = async (consent: "Accepted" | "Rejected") => {
    setActionLoading(true);
    try {
      const res = await OdrApi.respondOdr({
        caseId: numericCaseId,
        consentStatus: consent,
        responseNotes: consent === "Accepted"
          ? "I hereby submit consent to enter the collaborative NoLegalPaisa ODR Dialogue box room."
          : "I decline/contest the claimant allegations."
      });
      if (res?.success) {
        await loadData();
      } else {
        alert(res?.message || "Failed to update response consent status.");
      }
    } catch (err: any) {
      alert(err?.message || "Error submitting response.");
    } finally {
      setActionLoading(false);
    }
  };

  const getActiveStep = () => {
    if (!caseDetails) return 0;
    const status = caseDetails.odrStatus || "INITIATED";

    switch (status.toUpperCase()) {
      case "INITIATED":
        return 0;
      case "RESPONDED":
        return 1;
      case "TRIAGED":
        return 2;
      case "MEDIATION":
      case "ARBITRATION":
        return 3;
      case "RESOLVED":
      case "REJECTED":
      case "DEFAULT_AWARDED":
        return 4;
      default:
        return 0;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !caseDetails) {
    return (
      <Box px={3} py={5} sx={{ maxWidth: 800, mx: "auto" }}>
        <Alert severity="error" variant="filled" sx={{ borderRadius: "16px" }}>
          {error || "Dispute Workspace could not be loaded."}
        </Alert>
        <Button variant="outlined" sx={{ mt: 3, borderRadius: "10px" }} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  // Check if current user is Claimant or Respondent
  const isClaimant = myInfo?.id === caseDetails.clientId;
  const isRespondent = myInfo?.email === caseDetails.respondentEmail || myInfo?.phone === caseDetails.respondentPhone;
  const showNoticeBanner = isRespondent && caseDetails.respondentConsentStatus === "Pending";
  const isAdmin = ["ADMIN", "SUPERADMIN"].includes(myInfo?.userType?.toUpperCase() || "");
  
  const currentNeutral = neutralAssignment?.currentNeutral || (neutralAssignment?.isCurrentUserProposedNeutral ? neutralAssignment : null);
  const showNeutralConsentBanner = currentNeutral && currentNeutral.consentStatus === "Pending";

  const handleAssignNeutral = async () => {
    if (!selectedNeutralId) return;
    setActionLoading(true);
    try {
      const res = await OdrApi.assignOdrNeutral(numericCaseId, {
        neutralId: Number(selectedNeutralId)
      });
      if (res?.success) {
        alert("Neutral expert proposed successfully.");
        await loadData();
      } else {
        alert(res?.message || "Failed to assign neutral.");
      }
    } catch (err: any) {
      alert(err?.message || "Error assigning neutral.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleNeutralConsent = async () => {
    if (!disclosureText.trim()) {
      alert("Please provide a conflict-of-interest disclosure to accept the case.");
      return;
    }
    setActionLoading(true);
    try {
      const res = await OdrApi.acceptOdrNeutral(numericCaseId, {
        conflictDisclosureText: disclosureText.trim(),
      });
      // Service wrapper nests API response in res.data
      const payload = res?.data ?? res;
      if (payload?.consentStatus === "Confirmed" || payload?.message?.toLowerCase().includes("confirmed")) {
        await loadData();
      } else {
        alert(payload?.message || "Failed to accept neutral assignment.");
      }
    } catch (err: any) {
      alert(err?.message || "Error accepting neutral assignment.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnassignNeutral = async (professionalId: number) => {
    if (!window.confirm("Are you sure you want to remove this panel member?")) return;
    setActionLoading(true);
    try {
      const res = await AdminApi.UnassignCaseProfessional({
        caseId: numericCaseId,
        professionalId
      });
      if (res?.success) {
        await loadData();
      } else {
        alert(res?.message || "Failed to remove panel member.");
      }
    } catch (err: any) {
      alert(err?.message || "Error removing panel member.");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const s = (status || "").toUpperCase();
    if (["REJECTED", "CLOSED", "DECLINED", "FAILED"].includes(s)) {
      return { bgcolor: "#ef4444", color: "#ffffff" };
    }
    if (["INITIATED", "PENDING", "DRAFT", "AWAITING_CONSENT"].includes(s)) {
      return { bgcolor: "#f59e0b", color: "#ffffff" };
    }
    return { bgcolor: "#10b981", color: "#ffffff" };
  };

  return (
    <Box overflow="auto" height="100%" sx={{ py: 3, px: { xs: 2, md: 4 }, bgcolor: "#f8fafc" }}>
      {/* Workspace Header */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <Chip
              icon={<Scale />}
              label={`${caseDetails.odrType || "Online Dispute Resolution"}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, fontFamily: "Outfit", borderRadius: "8px" }}
            />
            <Chip
              label={`Ref: #${caseDetails.pkCase}`}
              sx={{ fontWeight: 700, bgcolor: "rgba(40, 49, 101, 0.08)", color: "#283165", borderRadius: "8px" }}
            />
            <Chip
              label={`Status: ${caseDetails.odrCustomStatus || caseDetails.odrStatus || caseDetails.currentProcessingStatusName || "INITIATED"}`}
              sx={{ 
                fontWeight: 800, 
                borderRadius: "8px", 
                px: 1,
                ...getStatusColor(caseDetails.odrCustomStatus || caseDetails.odrStatus || caseDetails.currentProcessingStatusName || "INITIATED")
              }}
            />
          </Stack>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", fontFamily: "Outfit" }}>
            ODR Interactive Dialogue Workspace
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Chat />}
            onClick={() => navigate(`/chat?openCase=${numericCaseId}`)}
            disabled={
              (isRespondent && showNoticeBanner) || 
              showNeutralConsentBanner || 
              caseDetails.respondentConsentStatus === "Rejected" || 
              caseDetails.respondentConsentStatus === "Declined" ||
              caseDetails.odrStatus === "REJECTED"
            }
            sx={{
              bgcolor: "#283165",
              color: "white",
              fontWeight: 700,
              px: 3,
              py: 1.2,
              borderRadius: "12px",
              boxShadow: "0 4px 14px rgba(40, 49, 101, 0.3)",
              "&:hover": { bgcolor: "#1f2650" },
              "&.Mui-disabled": { bgcolor: "#94a3b8", color: "#e2e8f0" }
            }}
          >
            Launch Dialogue Chat Room
          </Button>
        </Stack>
      </Box>

      {/* 🛡️ Professional Conflict Disclosure & Acceptance Banner */}
      {showNeutralConsentBanner && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: "24px",
            background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
            border: "1px solid #4f46e5",
            boxShadow: "0 10px 25px -5px rgba(79, 70, 229, 0.3)",
            color: "#ffffff"
          }}
        >
          <Stack direction="row" spacing={2.5} sx={{ mb: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "16px",
                bgcolor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Gavel sx={{ color: "#a5b4fc", fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: "Outfit", color: "#e0e7ff" }}>
                Neutral Panel Assignment Proposal & Conflict Disclosure Check
              </Typography>
              <Typography variant="body2" sx={{ color: "#c7d2fe", mt: 0.5, lineHeight: 1.6 }}>
                You have been proposed as the neutral expert (Mediator/Conciliator) for Case <strong>#{caseDetails.pkCase}</strong>. In accordance with the <strong>Mediation Act, 2023</strong>, please submit any required conflict-of-interest disclosure to formally accept this appointment and activate the dispute resolution chamber.
              </Typography>
            </Box>
          </Stack>
          
          <Divider sx={{ my: 3, borderColor: "rgba(255, 255, 255, 0.1)" }} />
          
          <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#e0e7ff" }}>
              Declaration / Conflict of Interest Disclosure Statement
            </Typography>
            <textarea
              value={disclosureText}
              onChange={(e) => setDisclosureText(e.target.value)}
              placeholder="E.g., I hereby declare that I have no personal, financial, or professional conflict of interest with either of the disputing parties that would interfere with my impartial resolution of this matter..."
              style={{
                width: "100%",
                height: "100px",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                color: "#ffffff",
                fontFamily: "Inter",
                fontSize: "14px",
                lineHeight: "1.5",
                resize: "none",
                outline: "none"
              }}
            />
            
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button
                variant="contained"
                disabled={actionLoading}
                onClick={handleNeutralConsent}
                sx={{
                  bgcolor: "#4f46e5",
                  color: "#ffffff",
                  borderRadius: "12px",
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontFamily: "Outfit",
                  textTransform: "none",
                  boxShadow: "0 4px 14px rgba(79, 70, 229, 0.4)",
                  "&:hover": {
                    bgcolor: "#4338ca"
                  }
                }}
              >
                Submit Disclosure & Accept Case
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* 👑 Admin Neutral Assignment Panel */}
      {isAdmin && caseDetails.odrStatus === "TRIAGED" && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: "24px",
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            border: "1px solid #334155",
            boxShadow: "0 10px 25px -5px rgba(15, 23, 42, 0.3)",
            color: "#ffffff"
          }}
        >
          <Stack direction="row" spacing={2.5} sx={{ mb: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "16px",
                bgcolor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Gavel sx={{ color: "#38bdf8", fontSize: 32 }} />
            </Box>
            <Box flex={1}>
              <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: "Outfit", color: "#f8fafc" }}>
                Admin Panel: Appoint Professional Neutral
              </Typography>
              <Typography variant="body2" sx={{ color: "#cbd5e1", mt: 0.5, lineHeight: 1.6 }}>
                The respondent has submitted ODR consent. Please assign an empanelled expert neutral (Mediator or Arbitrator) to this dispute. Once assigned, the neutral will review the conflict disclosure and activate the joint dialogue chamber.
              </Typography>
              {neutralAssignment && (
                <Box sx={{ mt: 2, p: 2, borderRadius: "12px", bgcolor: "rgba(56, 189, 248, 0.1)", border: "1px dashed rgba(56, 189, 248, 0.3)" }}>
                  <Typography variant="body2" sx={{ color: "#38bdf8", fontWeight: 700 }}>
                    Proposed Neutral: {neutralAssignment.firstName} {neutralAssignment.lastName} ({neutralAssignment.email}) (Status: {neutralAssignment.consentStatus || "Pending"})
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>

          <Divider sx={{ my: 3, borderColor: "rgba(255, 255, 255, 0.1)" }} />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <Box flex={1} width="100%">
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#cbd5e1", mb: 1 }}>
                Select Empanelled Neutral Expert
              </Typography>
              <select
                value={selectedNeutralId}
                onChange={(e) => setSelectedNeutralId(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "12px",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  color: "#ffffff",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  outline: "none"
                }}
              >
                <option value="" style={{ color: "#000" }}>-- Choose Professional Neutral --</option>
                {professionals.map((prof: any) => (
                  <option key={prof.id} value={prof.id} style={{ color: "#000" }}>
                    {prof.firstName} {prof.lastName} ({prof.email}) - {prof.highestQualification || "No Qual"}
                  </option>
                ))}
              </select>
            </Box>

            <Button
              variant="contained"
              disabled={actionLoading || !selectedNeutralId}
              onClick={handleAssignNeutral}
              sx={{
                alignSelf: { xs: "stretch", sm: "flex-end" },
                bgcolor: "#38bdf8",
                color: "#0f172a",
                borderRadius: "12px",
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontFamily: "Outfit",
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#0ea5e9"
                }
              }}
            >
              Appoint Neutral Expert
            </Button>
          </Stack>
        </Paper>
      )}

      {/* ⚠️ Respondent Action Consent Banner */}
      {showNoticeBanner && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: "24px",
            background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
            border: "1px solid #f59e0b",
            boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.1)"
          }}
        >
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Warning sx={{ color: "#d97706", fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ color: "#78350f", fontWeight: 700, fontFamily: "Outfit" }}>
                Pending ODR Dispute Notice Served Against You
              </Typography>
              <Typography variant="body2" sx={{ color: "#92400e", mt: 0.5 }}>
                Claimant <strong>{caseDetails.claimantName || "A valued user"}</strong> has filed an ODR case against you for a dispute regarding <strong>{caseDetails.description || "Unspecified details"}</strong>.
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2, borderColor: "rgba(217, 119, 6, 0.2)" }} />
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="body2" sx={{ color: "#78350f", fontWeight: 600 }}>
                Disputed Claim Amount: ₹{(caseDetails.claimAmount || 0).toLocaleString("en-IN")}
              </Typography>
              <Typography variant="caption" sx={{ color: "#d97706", display: "block" }}>
                Response Window Expiry: {new Date(caseDetails.noticeExpiryDate).toLocaleDateString()} (10-Day Clock)
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleRespondentConsent("Rejected")}
                disabled={actionLoading}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
              >
                Reject & Contest Claim
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => handleRespondentConsent("Accepted")}
                disabled={actionLoading}
                sx={{
                  bgcolor: "#d97706",
                  color: "white",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": { bgcolor: "#b45309" }
                }}
              >
                Accept Consent & Open Workspace
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      {/* Stepper Timeline Tracker */}
      <PremiumTimelineTracker
        currentStatus={caseDetails.odrStatus || "INITIATED"}
        claimAmount={caseDetails.claimAmount || 0}
        complexityScore={caseDetails.complexityScore || 0}
        sessions={sessions}
        currentUserId={myInfo?.id || 0}
        isAdmin={["ADMIN", "SUPERADMIN"].includes(myInfo?.userType?.toUpperCase() || "")}
        isNeutral={myInfo?.userType === "PROFESSIONAL" && myInfo?.id === caseDetails.assignedProfessionalId}
        neutralAssignment={neutralAssignment}
      />

      {/* Core Grid */}
      <Grid container spacing={4}>
        {/* Left Hand Details Column */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Stack spacing={4}>
            {/* Case Details Card */}
            <Card elevation={0} sx={{ borderRadius: "24px", border: "1px solid #e2e8f0" }}>
              <CardContent sx={{ p: 3.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "Outfit", color: "#1e293b", mb: 2 }}>
                  Dispute Overview
                </Typography>
                <Grid container spacing={3}>
                  {/* Claimant Details */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                        <Shield sx={{ fontSize: 18, color: "#3b82f6" }} /> Claimant Details
                      </Typography>
                      
                      <Typography variant="caption" color="text.secondary" display="block">Name</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", mb: 1 }}>{caseDetails.claimantName || "Not Sync'd"}</Typography>
                      
                      <Typography variant="caption" color="text.secondary" display="block">Email</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", mb: 1 }}>{caseDetails.claimantEmail || "Not Sync'd"}</Typography>
                      
                      <Typography variant="caption" color="text.secondary" display="block">Phone Number</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>{caseDetails.claimantPhone || "Not Sync'd"}</Typography>
                    </Box>
                  </Grid>

                  {/* Respondent Details */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                        <Gavel sx={{ fontSize: 18, color: "#ef4444" }} /> Respondent Details
                      </Typography>
                      
                      <Typography variant="caption" color="text.secondary" display="block">Name</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", mb: 1 }}>{caseDetails.respondentName || "Not Sync'd"}</Typography>
                      
                      <Typography variant="caption" color="text.secondary" display="block">Email</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", mb: 1 }}>{caseDetails.respondentEmail || "Not Sync'd"}</Typography>
                      
                      <Typography variant="caption" color="text.secondary" display="block">Phone Number</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>{caseDetails.respondentPhone || "Not Sync'd"}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.primary" display="block">
                      Disputed Value
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#283165", fontFamily: "Inter" }}>
                      ₹{(caseDetails.claimAmount || 0).toLocaleString("en-IN")}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.primary" display="block">
                      Dispute Resolution Cost
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#10b981" }}>
                      Free (Zero Fees)
                    </Typography>
                  </Grid>
                  {caseDetails.assignedProfessionals && caseDetails.assignedProfessionals.length > 0 ? (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.primary" display="block" sx={{ mb: 1 }}>
                        Assigned ODR Neutrals / Panel
                      </Typography>
                      <Stack spacing={1}>
                        {caseDetails.assignedProfessionals.map((prof: any) => (
                          <Paper
                            key={prof.professionalId}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: "12px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderColor: "#e2e8f0"
                            }}
                          >
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: "#283165" }}>
                                {prof.fullName} ({prof.email})
                              </Typography>
                              <Box display="flex" gap={1} mt={0.5}>
                                <Chip
                                  label={prof.role || "Neutral"}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  sx={{ fontWeight: 600, borderRadius: "6px" }}
                                />
                                <Chip
                                  label={prof.consentStatus === "Confirmed" || prof.consentStatus === "Accepted" ? "Confirmed" : "Pending"}
                                  color={prof.consentStatus === "Confirmed" || prof.consentStatus === "Accepted" ? "success" : "warning"}
                                  size="small"
                                  sx={{ fontWeight: 600, borderRadius: "6px" }}
                                />
                              </Box>
                            </Box>
                            {isAdmin && (
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                disabled={actionLoading}
                                onClick={() => handleUnassignNeutral(prof.professionalId)}
                                sx={{ borderRadius: "8px", textTransform: "none" }}
                              >
                                Remove
                              </Button>
                            )}
                          </Paper>
                        ))}
                      </Stack>
                    </Grid>
                  ) : neutralAssignment ? (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.primary" display="block">
                          Assigned ODR Neutral
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: "#283165" }}>
                          {neutralAssignment.firstName} {neutralAssignment.lastName}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.primary" display="block">
                          Neutral Consent Status
                        </Typography>
                        <Chip 
                          label={neutralAssignment.consentStatus === "Confirmed" ? "Confirmed" : "Pending Acceptance"} 
                          color={neutralAssignment.consentStatus === "Confirmed" ? "success" : "warning"}
                          size="small"
                          sx={{ fontWeight: 600, borderRadius: "6px", mt: 0.5 }}
                        />
                      </Grid>
                    </>
                  ) : null}
                </Grid>
                <Divider sx={{ my: 2.5 }} />
                <Typography variant="caption" color="text.primary" display="block">
                  Case Brief & Description
                </Typography>
                <Typography variant="body2" sx={{ color: "#475569", mt: 0.5, lineHeight: 1.6 }}>
                  {caseDetails.description}
                </Typography>
              </CardContent>
            </Card>

            {/* Document Vault Drawer */}
            <Card elevation={0} sx={{ borderRadius: "24px", border: "1px solid #e2e8f0" }}>
              <CardContent sx={{ p: 3.5 }}>
                <Stack direction="row" justifyItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "Outfit", color: "#1e293b" }}>
                    Secured Case Documents Vault
                  </Typography>
                  <Chip
                    icon={<Shield />}
                    label="Secured by SHA256 Vault"
                    color="success"
                    size="small"
                    sx={{ borderRadius: "6px", fontWeight: 600 }}
                  />
                </Stack>

                <Stack spacing={2}>
                  {/* Notice Download Block */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderColor: "rgba(40, 49, 101, 0.15)",
                      bgcolor: "rgba(40, 49, 101, 0.02)"
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "12px",
                          bgcolor: "rgba(40, 49, 101, 0.08)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <InsertDriveFile sx={{ color: "#283165" }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                          Dispute Notice Brief (Notice PDF)
                        </Typography>
                        <Typography variant="caption" color="text.primary">
                          Auto-generated QuestPDF Case Initiation Brief
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<FileDownload />}
                      onClick={handleDownloadNotice}
                      sx={{ bgcolor: "#283165", textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
                    >
                      Download Notice
                    </Button>
                  </Paper>

                  {/* Dynamic documents uploaded */}
                  {documents.length > 0 ? (
                    documents.map((doc, idx) => (
                      <Paper
                        key={doc.id || idx}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: "16px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          borderColor: doc.isConfidential ? "#fca5a5" : "#e2e8f0",
                          bgcolor: doc.isConfidential ? "#fff5f5" : "transparent"
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "12px",
                              bgcolor: doc.isConfidential ? "#fee2e2" : "#f1f5f9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            <InsertDriveFile sx={{ color: doc.isConfidential ? "#ef4444" : "#64748b" }} />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                              {doc.fileName}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Uploaded on {new Date(doc.uploadTimestamp).toLocaleDateString()} ({doc.documentType})
                              </Typography>
                              {doc.isConfidential && (
                                <Chip
                                  label="Confidential Caucus"
                                  size="small"
                                  color="error"
                                  sx={{ height: 16, fontSize: "0.6rem", fontWeight: 700, borderRadius: "4px" }}
                                />
                              )}
                            </Stack>
                          </Box>
                        </Stack>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FileDownload />}
                          onClick={() => handleDownloadDocument(doc.filePath, doc.fileName)}
                          sx={{
                            borderColor: doc.isConfidential ? "#ef4444" : "#283165",
                            color: doc.isConfidential ? "#ef4444" : "#283165",
                            textTransform: "none",
                            borderRadius: "8px",
                            fontWeight: 600,
                            "&:hover": {
                              borderColor: doc.isConfidential ? "#dc2626" : "#1f2650",
                              bgcolor: doc.isConfidential ? "#fee2e2" : "rgba(40, 49, 101, 0.04)"
                            }
                          }}
                        >
                          Download
                        </Button>
                      </Paper>
                    ))
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Right Hand Video Caucus Drawer */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={4}>
            {/* Live Caucus Meeting scheduler card */}
            <Card
              elevation={0}
              sx={{
                borderRadius: "24px",
                border: "1px solid #e2e8f0",
                background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)"
              }}
            >
              <CardContent sx={{ p: 3.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <VideoCall color="primary" sx={{ fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "Outfit", color: "#1e293b" }}>
                    Secured Video Caucus Rooms
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.primary" sx={{ mb: 3 }}>
                  Launch or join fully encrypted, secure ODR caucus rooms scheduled by your assigned neutral.
                </Typography>

                {sessions.length > 0 ? (
                  sessions.map((sess, idx) => (
                    <Paper
                      key={sess.id || idx}
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        borderRadius: "16px",
                        bgcolor: "rgba(16, 185, 129, 0.02)",
                        borderColor: "rgba(16, 185, 129, 0.2)",
                        mb: 2
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#065f46" }}>
                        {sess.sessionType === "PrivateCaucus" ? "🔒 Private Caucus Meeting" : "👥 Joint ODR Conference"}
                      </Typography>
                      <Typography variant="caption" color="text.primary" display="block" sx={{ mt: 0.5, mb: 2 }}>
                        Scheduled Time: {new Date(sess.scheduledStartTime).toLocaleString()}
                      </Typography>

                      {sess.isActive && (
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          onClick={() => setActiveCall({
                            appId: "0f39c85819e548e3883eff3ccad57e53", // Standard Agora AppId
                            channelName: sess.agoraChannelName,
                            token: sess.agoraToken,
                            uid: Math.floor(Math.random() * 10000)
                          })}
                          sx={{
                            bgcolor: "#10b981",
                            fontWeight: 700,
                            borderRadius: "10px",
                            textTransform: "none",
                            "&:hover": { bgcolor: "#059669" }
                          }}
                        >
                          Join Secure Call Room
                        </Button>
                      )}
                    </Paper>
                  ))
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      textAlign: "center",
                      borderStyle: "dashed",
                      borderColor: "#cbd5e1"
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#94a3b8", fontWeight: 500 }}>
                      No active sessions scheduled. Check the dialogue room chat for updates from the neutral.
                    </Typography>
                  </Paper>
                )}
              </CardContent>
            </Card>

            {/* Platform rules advisory card */}
            <Card elevation={0} sx={{ borderRadius: "24px", bgcolor: "#1e293b", color: "white" }}>
              <CardContent sx={{ p: 3.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Scale sx={{ color: "#38bdf8" }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "Outfit", color: "white" }}>
                    Legal Framework Advisory
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: "#94a3b8", lineHeight: 1.6 }}>
                  This resolution process is legally enforceable in accordance with the <strong>Arbitration & Conciliation Act 1996</strong> and the <strong>Mediation Act 2023</strong>. Both parties maintain absolute confidentiality throughout.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Floating Agora Call Portal */}
      {activeCall && (
        <AgoraCall
          appId={activeCall.appId}
          channelName={activeCall.channelName}
          token={activeCall.token}
          uid={activeCall.uid}
          onClose={() => setActiveCall(null)}
        />
      )}
    </Box>
  );
};

export default OdrWorkspace;
